// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.RoleListStore.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import { observable, action, computed } from 'mobx';
import { isNil, filter, every, some, forEach } from 'lodash/fp';
import PagingModel from 'share/components/controls/DataGrid/helpers/Izenda.PagingModel';
import { DEFAULT_VALUE } from 'logic/data/Izenda.UserAccountConstants';
import UiState from 'logic/Izenda.UiState';
import fetcher from 'fetchers/Izenda.RoleSetupFetcher';

export default class RoleListStore {
  @observable roles: Array<any> = [];
  @observable searchText: string = '';
  @observable pageInfo: PagingModel = new PagingModel({ name: 'DESC' });

  @computed
  get tenantId() {
    return UiState.getTenantId();
  }

  @computed
  get selectedRoles() {
    return filter(role => role.selected, this.roles);
  }

  @computed
  get allCheckboxStatus() {
    if (!this.roles.length) {
      return { checked: false };
    }
    if (every('selected', this.roles)) {
      return { checked: true };
    }
    if (some('selected', this.roles)) {
      return { indeterminate: true };
    }
    return { checked: false };
  }

  constructor(loadRoles, roleOption = {}) {
    this.loadRoles = loadRoles;
    this.roleOption = roleOption;
  }

  /**
   * Load all user accounts
   */
  @action
  loadAllRoles(): any {
    this.pageInfo.buildPagingRequest();
    const pageInfo = this.pageInfo;
    const requestData = {
      ...this.roleOption,
      criteria: pageInfo.searchCriteria || [],
      pageIndex: pageInfo.pageIndex || 1,
      pageSize: pageInfo.pageSize || 10,
      sortOrders: pageInfo.sortOrders || [
        {
          key: 'name',
          descending: true
        }
      ]
    };

    const fnFetch = isNil(this.loadRoles)
      ? fetcher.loadAllowedSharingRolesPaging(requestData)
      : this.loadRoles(pageInfo);

    return fnFetch.then(data => {
      this.roles = data.result;
      this.pageInfo.totalRecords = data.total;
      return Promise.resolve(true);
    });
  }

  /**
   * Set search value
   * @param {string} value
   */
  @action
  setSearchValue(event: any) {
    this.searchText = event.target.value;
  }

  /**
   * Toggle All Roles
   */
  @action
  toggleAllRoles(event: any) {
    forEach(item => {
      item.selected = event.target.checked;
    }, this.roles);
  }

  /**
   * Handle when page size change
   */
  @action
  onPageSizeChange(pageSize: number): any {
    this.pageInfo.setPageSize(pageSize);
    return this.goToPage(1);
  }

  /**
   * Handle go to page
   * @param  {int} pageIndex
   */
  @action
  goToPage(pageIndex: number): any {
    this.pageInfo.setPageIndex(pageIndex);
    return this.loadAllRoles();
  }

  /**
   * Search subscription
   * @param  {string} searchProperty
   * @param  {string} searchText
   */
  @action
  search(): any {
    this.pageInfo.setSearchValues('name', this.searchText);
    return this.goToPage(1);
  }

  /**
   * Change sort orders
   * @param {string} columnKey
   * @param {object} sortDir
   */
  @action
  onSortChange(columnKey: string, sortDir: any): any {
    this.pageInfo.setColSortDirs(columnKey, sortDir);
    return this.loadAllRoles();
  }
}



// WEBPACK FOOTER //
// ./ui/logic/Izenda.RoleListStore.js